<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    button-location="top"
    @submit="search"
  >
    <template #title>
      QA Calibrations
    </template>

    <template #top-form>
      <p class="text-muted">
        {{ pageHint }}
      </p>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Submitted </label>
        <div class="col-sm-6">
          <date-picker
            v-model="dateRange"
            type="daterange"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> Dispute </label>
        <div class="col-sm-6">
          <dropdown-list
            v-model="disputeState"
            clearable
            :options="DISPUTE_FILTER_OPTIONS"
          />
        </div>
      </div>

      <template v-if="canSeeAllCalibrations">
        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Reviewer </label>
          <div class="col-sm-6">
            <dropdown-list
              v-model="reviewerId"
              :options="reviewerOptions"
              filterable
              clearable
            />
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Operator </label>
          <div class="col-sm-6">
            <dropdown-list
              v-model="operatorId"
              :options="operatorOptions"
              filterable
              remote
              clearable
              :remote-method="searchOperators"
              :loading="isSearchingOperators"
            />
          </div>
        </div>
      </template>

      <div class="form-submit-button-group pb-10">
        <p-button
          type="purple"
          round
          native-type="submit"
          :disabled="status.isLoading"
        >
          Search
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="row align-items-center">
        <pagination
          :is-loading="status.isLoading"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="loadCalibrations"
        />
      </div>
    </template>

    <template #content>
      <table-view-container
        :page-id="pageId"
        :data="tableData"
        :table-columns="tableColumns"
      >
        <template #right-fixed-columns>
          <el-table-column
            fixed="right"
            label="Actions"
            width="140"
            align="center"
          >
            <template #default="scope">
              <p-button
                v-if="scope.row.canRaiseDispute"
                type="purple"
                size="sm"
                round
                @click="openRaise(scope.row)"
              >
                Raise dispute
              </p-button>
              <p-button
                v-if="scope.row.canResolveDispute"
                type="purple"
                size="sm"
                round
                @click="openResolve(scope.row)"
              >
                Settle
              </p-button>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>
  </card-container>

  <raise-qa-dispute-dialog
    :is-show-dialog="raiseDialog.visible"
    :loading="raiseDialog.loading"
    @cancel="raiseDialog.visible = false"
    @submit="submitRaise"
  />

  <resolve-qa-dispute-dialog
    :is-show-dialog="resolveDialog.visible"
    :loading="resolveDialog.loading"
    :reason="resolveDialog.reason"
    @cancel="resolveDialog.visible = false"
    @submit="submitResolve"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import Moment from 'moment-timezone';
import { uniqBy } from 'lodash';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { useSwalDialog } from '@/use/swalDialog';
import type { ListType } from '@/models/app';
import type { TableColumnType } from '@/models/table';
import type { QaCalibrationDisputeFilter } from '@/gql/graphql';
import { qaCalibrationService } from '@/services/qaCalibration';
import type { QaCalibrationRow } from '@/services/qaCalibration/fetchQaCalibrations';
import { operatorService } from '@/services/operators';
import RaiseQaDisputeDialog from './RaiseQaDisputeDialog.vue';
import ResolveQaDisputeDialog from './ResolveQaDisputeDialog.vue';
import { DISPUTE_FILTER_OPTIONS, disputeLabel, yesNoLabel } from './qaCalibrations';

const pageId = 'qa-calibrations';
const tablePaginationLimit = 30;
const userSearchLimit = 50;

const { userRoles } = useProfile();
const { showErrorPopupDialog, showSuccessPopupDialog } = useSwalDialog();

const canSeeAllCalibrations = computed(
  () => userRoles.isAdmin || userRoles.isDirector || userRoles.isSupervisor || userRoles.isSupervisorTeamLead || userRoles.isGourmetSupervisor,
);

const pageHint = computed(() =>
  canSeeAllCalibrations.value
    ? 'Every stored calibration. Settle open disputes from the Actions column.'
    : 'The calibrations you submitted. Raise a dispute if you want a supervisor to decide whether the AI got the call wrong.',
);

const dateRange = ref<[Date, Date] | null>(null);
const disputeState = ref<QaCalibrationDisputeFilter | ''>('');
const reviewerId = ref('');
const operatorId = ref('');
const reviewerOptions = ref<ListType[]>([]);
const operatorOptions = ref<ListType[]>([]);
const isSearchingOperators = ref(false);
const tableData = ref<ReturnType<typeof toTableRow>[]>([]);

const status = reactive({
  spinnerMessage: '',
  isLoading: false,
});
const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});
const raiseDialog = reactive({
  visible: false,
  loading: false,
  calibrationId: 0,
});
const resolveDialog = reactive({
  visible: false,
  loading: false,
  calibrationId: 0,
  reason: '',
});

const tableColumns: TableColumnType[] = [
  { field: 'createdAt', label: 'Submitted', width: 160, isDateField: true },
  { field: 'callLogId', label: 'Call Log', width: 100 },
  { field: 'source', label: 'Found In', width: 170, isPlainText: true },
  { field: 'submittedByName', label: 'Reviewer', width: 150, isPlainText: true },
  { field: 'operatorName', label: 'Operator', width: 150, isPlainText: true },
  { field: 'originalScore', label: 'Original Score', width: 110, isPlainText: true },
  { field: 'reviewerScore', label: 'Reviewer Score', width: 110, isPlainText: true },
  { field: 'isAiCorrectLabel', label: 'AI Correct?', width: 100, isCenterAlign: true },
  { field: 'criticalErrorLabel', label: 'AI Missed Critical Error?', width: 120, isCenterAlign: true },
  { field: 'aiFeedback', label: 'AI Feedback', minWidth: 200, isReadMore: true, isPlainText: true },
  { field: 'disputeLabel', label: 'Dispute', width: 90, isCenterAlign: true },
  { field: 'disputedAt', label: 'Dispute Raised', width: 160, isDateField: true },
  { field: 'disputeReason', label: 'Dispute Reason', minWidth: 200, isReadMore: true, isPlainText: true },
  { field: 'verdictLabel', label: 'AI Error? (verdict)', width: 120, isCenterAlign: true },
  { field: 'resolutionNote', label: 'Settlement Note', minWidth: 200, isReadMore: true, isPlainText: true },
  { field: 'resolvedByName', label: 'Settled By', width: 150, isPlainText: true },
  { field: 'resolvedAt', label: 'Settled At', width: 160, isDateField: true },
];

const toTableRow = (row: QaCalibrationRow) => ({
  ...row,
  disputedAt: row.dispute?.disputedAt ?? null,
  isAiCorrectLabel: yesNoLabel(row.isAiCorrect),
  criticalErrorLabel: yesNoLabel(row.aiMissedCriticalError),
  disputeLabel: disputeLabel(row),
  disputeReason: row.dispute?.reason ?? '',
  verdictLabel: yesNoLabel(row.dispute?.wasAiError),
  resolutionNote: row.dispute?.resolutionNote ?? '',
  resolvedByName: row.dispute?.resolvedByName ?? '',
  resolvedAt: row.dispute?.resolvedAt ?? null,
});

const buildFilter = () => ({
  createdFrom: dateRange.value ? dateRange.value[0] : undefined,
  createdTo: dateRange.value ? Moment(dateRange.value[1]).endOf('day').toDate() : undefined,
  disputeState: disputeState.value || undefined,
  submittedByUserId: reviewerId.value || undefined,
  operatorId: operatorId.value || undefined,
});

// Paging and post-action refreshes reuse the last searched filter, not unsubmitted form edits.
const appliedFilter = ref(buildFilter());

const search = () => {
  appliedFilter.value = buildFilter();
  loadCalibrations();
};

const loadCalibrations = async (currentPage: number = 1) => {
  paginationInfo.currentPage = currentPage;
  status.spinnerMessage = 'Loading calibrations...';
  status.isLoading = true;

  try {
    const response = await qaCalibrationService.fetchQaCalibrations({
      filter: appliedFilter.value,
      pagination: { limit: tablePaginationLimit, offset: (currentPage - 1) * tablePaginationLimit },
    });

    paginationInfo.totalPages = Math.ceil(response.count / tablePaginationLimit) || 1;
    if (currentPage > paginationInfo.totalPages) {
      return await loadCalibrations(paginationInfo.totalPages);
    }
    tableData.value = response.rows.map(toTableRow);
  } catch (error) {
    tableData.value = [];
    paginationInfo.currentPage = 1;
    paginationInfo.totalPages = 1;
    showErrorPopupDialog({ text: util.getGraphqlError(error, false) });
  } finally {
    status.isLoading = false;
  }
};

const searchUsers = async (query: string, isSearching: Ref<boolean>): Promise<ListType[]> => {
  if (!query.trim()) {
    return [];
  }

  isSearching.value = true;
  try {
    const result = await operatorService.fetchOperatorOptions({ query, pagination: { limit: userSearchLimit, offset: 0 } });
    return result.rows.map((row, index) => ({ id: index + 1, key: row.id, label: util.getFullName(row.firstName, row.lastName) }));
  } catch (error) {
    showErrorPopupDialog({ text: util.getGraphqlError(error, false) });
    return [];
  } finally {
    isSearching.value = false;
  }
};

const keepSelected = (options: ListType[], current: ListType[], selectedId: string) =>
  uniqBy([...current.filter((option) => option.key === selectedId), ...options], 'key');

const loadReviewerOptions = async () => {
  try {
    const reviewers = await qaCalibrationService.fetchQaCalibrationReviewers();
    reviewerOptions.value = reviewers.map((reviewer, index) => ({ id: index + 1, key: reviewer.id, label: reviewer.name }));
  } catch (error) {
    showErrorPopupDialog({ text: util.getGraphqlError(error, false) });
  }
};

const searchOperators = async (query: string) => {
  operatorOptions.value = keepSelected(await searchUsers(query, isSearchingOperators), operatorOptions.value, operatorId.value);
};

const openRaise = (row: QaCalibrationRow) => {
  raiseDialog.calibrationId = row.id;
  raiseDialog.visible = true;
};

const openResolve = (row: QaCalibrationRow) => {
  resolveDialog.calibrationId = row.id;
  resolveDialog.reason = row.dispute?.reason ?? '';
  resolveDialog.visible = true;
};

const submitRaise = async (reason: string) => {
  raiseDialog.loading = true;
  try {
    await qaCalibrationService.raiseQaCalibrationDispute({ qaCalibrationResponseId: raiseDialog.calibrationId, reason });
    raiseDialog.visible = false;
    showSuccessPopupDialog({ text: 'Dispute raised.' });
    await loadCalibrations(paginationInfo.currentPage);
  } catch (error) {
    showErrorPopupDialog({ text: util.getGraphqlError(error, false) });
  } finally {
    raiseDialog.loading = false;
  }
};

const submitResolve = async ({ wasAiError, note }: { wasAiError: boolean; note: string }) => {
  resolveDialog.loading = true;
  try {
    await qaCalibrationService.resolveQaCalibrationDispute({ qaCalibrationResponseId: resolveDialog.calibrationId, wasAiError, note });
    resolveDialog.visible = false;
    showSuccessPopupDialog({ text: 'Dispute settled.' });
    await loadCalibrations(paginationInfo.currentPage);
  } catch (error) {
    showErrorPopupDialog({ text: util.getGraphqlError(error, false) });
  } finally {
    resolveDialog.loading = false;
  }
};

const resetForm = () => {
  dateRange.value = null;
  disputeState.value = '';
  reviewerId.value = '';
  operatorId.value = '';
  search();
};

onMounted(() => {
  search();
  if (canSeeAllCalibrations.value) {
    loadReviewerOptions();
  }
});
</script>
